import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import {
  IonBackButton,
  IonBadge,
  IonButtons,
  IonHeader,
  IonMenuButton,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';

import { Client } from '@core/interfaces';
import { ClientStore } from '@core/stores/client.store';
import { ScreenStore } from '@core/stores/screen.store';

@Component({
  selector: 'app-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonBadge,
    IonMenuButton,
    IonBackButton,
    IonTitle,
  ],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  host: {
    '[class.no-menu]': 'screen.showBottomNav()',
  },
})
export class AppHeaderComponent {
  private readonly router = inject(Router);
  readonly clientStore = inject(ClientStore);
  protected readonly screen = inject(ScreenStore);

  readonly showClientSelector = input(false);
  readonly title = input<string | null>(null);
  readonly backHref = input<string | null>(null);

  readonly showToolbar = computed(
    () =>
      !!this.title() ||
      !!this.backHref() ||
      this.showClientSelector() ||
      !this.screen.showBottomNav(),
  );

  private readonly failedLogos = signal<ReadonlySet<string>>(new Set());

  readonly primaryClient = computed<Client | null>(
    () => this.clientStore.selectedClients()[0] ?? null,
  );

  readonly selectionCountBadge = computed(() => {
    if (this.clientStore.allSelected() || this.clientStore.noneSelected()) {
      return '';
    }
    return `${this.clientStore.selectedClientUuids().length}/${this.clientStore.clients().length}`;
  });

  hasPrimaryClientLogo(): boolean {
    const client = this.primaryClient();
    if (!client?.logo) {
      return false;
    }
    return !this.failedLogos().has(client.uuid);
  }

  onPrimaryClientLogoError(): void {
    const client = this.primaryClient();
    if (!client) {
      return;
    }
    const nextFailedLogos = new Set(this.failedLogos());
    nextFailedLogos.add(client.uuid);
    this.failedLogos.set(nextFailedLogos);
  }

  getSelectorInitials(): string {
    const label = this.clientStore.selectionLabel().trim();
    if (!label) {
      return '?';
    }
    if (label === 'All') {
      return 'ALL';
    }
    const words = label.split(/\s+/).slice(0, 2);
    return words.map((word) => word[0]?.toUpperCase() ?? '').join('');
  }

  async openClientFilter(): Promise<void> {
    if (!this.clientStore.hasMultipleClients()) {
      return;
    }
    await this.router.navigateByUrl('/clients');
  }
}
